import { useEffect, useState } from "react";
import { portfolio } from "../data/portfolio";
import { SectionHead } from "../components/SectionHead";

type Status = "loading" | "live" | "fallback";
type Post = (typeof portfolio.posts)[number];

const MEDIUM_USER = "mohankrishnagr08";
const SUBSTACK_USER = "mohankrishnagr";
const PROFILE_URL = `https://medium.com/@${MEDIUM_USER}`;

function decode(s: string) {
  const ta = document.createElement("textarea");
  ta.innerHTML = s || "";
  return ta.value;
}

function extractFromContent(html: string): { image: string | null; paragraph: string } {
  if (!html) return { image: null, paragraph: "" };
  const doc = new DOMParser().parseFromString(html, "text/html");
  let image: string | null = null;
  const firstImg = doc.querySelector("img");
  if (firstImg) {
    const src = firstImg.getAttribute("src");
    if (src) image = src.replace(/\/v2\/resize:[^/]+\//, "/v2/resize:fill:800:540/");
  }
  let paragraph = "";
  const paras = doc.querySelectorAll("p");
  for (const p of Array.from(paras)) {
    const t = (p.textContent || "").trim();
    if (t.length > 40) {
      paragraph = t;
      break;
    }
  }
  if (!paragraph && paras[0]) paragraph = (paras[0].textContent || "").trim();
  return { image, paragraph };
}

interface RSSItem {
  title: string;
  pubDate: string;
  link: string;
  content?: string;
  description?: string;
  thumbnail?: string;
  categories?: string[];
}

// Each parsed post carries a numeric `ts` (the publication timestamp) so posts
// from different feeds can be interleaved in true chronological order before
// the `ts` is dropped and the plain `Post` handed to the UI.
function parseItems(items: RSSItem[], fallbackTag: string): { post: Post; ts: number }[] {
  return items.map((it) => {
    const { image, paragraph } = extractFromContent(it.content || it.description || "");
    const text =
      paragraph ||
      (it.description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const kicker = decode(text.length > 240 ? text.slice(0, 240).trim() + "…" : text);
    const words = (it.content || it.description || "")
      .replace(/<[^>]+>/g, " ")
      .split(/\s+/)
      .filter(Boolean).length;
    const readtime = Math.max(1, Math.round(words / 220)) + " min";
    const d = new Date(it.pubDate);
    const ts = isNaN(d.valueOf()) ? 0 : d.valueOf();
    const date = isNaN(d.valueOf()) ? "" : d.toLocaleString("en-US", { month: "short", year: "numeric" });
    const tag = decode((it.categories && it.categories[0]) || fallbackTag);
    return {
      post: {
        title: decode(it.title),
        kicker,
        date,
        readtime,
        tag,
        href: it.link,
        image: image || it.thumbnail || "",
      },
      ts,
    };
  });
}

// Turn a post's display date ("May 2026") into a sortable timestamp so the
// static extra posts can be interleaved with the live feeds by date.
function tsFromDate(date: string): number {
  const d = new Date(date);
  return isNaN(d.valueOf()) ? 0 : d.valueOf();
}

// Fetch + parse one RSS feed through the rss2json proxy. Returns [] on any
// failure so one dead feed never takes the whole section down.
async function fetchFeed(feedUrl: string, fallbackTag: string) {
  const proxied = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feedUrl)}`;
  try {
    const res = await fetch(proxied);
    const json = await res.json();
    if (json.status !== "ok" || !Array.isArray(json.items)) return [];
    return parseItems(json.items, fallbackTag);
  } catch {
    return [];
  }
}

export function Writing() {
  // Static fallback: extra (LinkedIn, etc.) + cached posts, newest-first.
  const initial = [...portfolio.extraPosts, ...portfolio.posts]
    .sort((a, b) => tsFromDate(b.date) - tsFromDate(a.date))
    .slice(0, 8);
  const [posts, setPosts] = useState<Post[]>(initial);
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [medium, substack] = await Promise.all([
        fetchFeed(`https://medium.com/feed/@${MEDIUM_USER}`, "Medium"),
        fetchFeed(`https://${SUBSTACK_USER}.substack.com/feed`, "Substack"),
      ]);
      if (cancelled) return;
      const live = [...medium, ...substack];
      if (live.length === 0) {
        setStatus("fallback");
        return;
      }
      // Interleave static extra posts with both live feeds, sort everything
      // newest-first by date, then drop the sort key.
      const merged = [
        ...portfolio.extraPosts.map((post) => ({ post, ts: tsFromDate(post.date) })),
        ...live,
      ]
        .sort((a, b) => b.ts - a.ts)
        .map((x) => x.post)
        .slice(0, 8);
      setPosts(merged);
      setStatus("live");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="writing" className="md-section">
      <div className="md-container">
        <SectionHead
          num="09"
          eyebrow="Writing"
          title="Notes on what I'm building."
          dek="Long-form on Medium and Substack. Pulled live; cached snapshot below if the feeds are asleep."
        />

        <div className="m3-writing__meta">
          <a
            className={"m3-writing__status m3-writing__status--" + status + " md-label-l"}
            href={PROFILE_URL}
            target="_blank"
            rel="noreferrer"
          >
            {status === "live" && (
              <>
                <span className="m3-writing__pulse" />
                <span>Live · Medium + Substack</span>
              </>
            )}
            {status === "loading" && (
              <>
                <span className="m3-spinner" />
                <span>Fetching from Medium &amp; Substack…</span>
              </>
            )}
            {status === "fallback" && (
              <>
                <span className="material-symbols-rounded icon-20">cloud_off</span>
                <span>Cached · Medium + Substack</span>
              </>
            )}
            <span className="material-symbols-rounded icon-20">arrow_outward</span>
          </a>
          <span className="md-label-m dim">
            {posts.length} {posts.length === 1 ? "post" : "posts"}
          </span>
        </div>

        <div className="m3-posts">
          {posts.map((p, i) => (
            <a
              key={(p.href || p.title) + i}
              href={p.href || "#"}
              target={p.href ? "_blank" : "_self"}
              rel="noreferrer"
              className="md-card md-card--outlined md-card--interactive m3-post"
            >
              <div className="m3-post__a">
                <div className="m3-post__thumb">
                  {p.image ? (
                    <img
                      src={
                        // Absolute URLs (Medium, LinkedIn CDN) used as-is;
                        // relative paths get Vite's BASE_URL prefix so they
                        // resolve to public/<path> on both root and project pages.
                        /^https?:\/\//.test(p.image)
                          ? p.image
                          : `${import.meta.env.BASE_URL}${p.image}`
                      }
                      alt=""
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="m3-post__thumb-fallback">
                      <span className="material-symbols-rounded">article</span>
                    </div>
                  )}
                </div>
                <div className="m3-post__body">
                  <div className="md-label-m m3-post__meta">
                    <span>{p.date}</span>
                    {p.readtime && (
                      <>
                        <span>·</span>
                        <span>{p.readtime}</span>
                      </>
                    )}
                    {p.tag && (
                      <span
                        className="md-chip md-chip--assist"
                        style={{ height: 22, padding: "0 8px", fontSize: 11 }}
                      >
                        {p.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="md-title-l m3-post__title">{p.title}</h3>
                  <p className="md-body-m m3-post__kicker">{p.kicker}</p>
                </div>
                <div className="m3-post__arrow">
                  <span className="material-symbols-rounded icon-20">arrow_outward</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
